import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CheckCircle,
  Eye,
  ExternalLink,
  Loader2,
  Phone,
  Users,
  XCircle,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { MODULES } from '../../constants/modules';
import { Button, EmptyState, InfoBanner, Modal, StatusPill } from '../../components/ui';
import { resolveUploadUrl } from '../../utils/uploadUrl';
import {
  getTournamentRegistrations,
  reviewTournamentRegistration,
} from './tournamentApi';

const FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All teams' },
];

const formatDate = (value) => value
  ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

const formatAvailabilityDate = (value) => value
  ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
  : '—';

function availabilityDates(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeExternalUrl(value) {
  if (!value) return null;
  try {
    const url = new URL(value);
    return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function DetailItem({ label, children, className = '' }) {
  if (children == null || children === '') return null;
  return (
    <div className={className}>
      <dt className="text-xs font-semibold uppercase tracking-wide text-ga-text-muted">{label}</dt>
      <dd className="mt-1 text-sm text-ga-text-primary">{children}</dd>
    </div>
  );
}

function RegistrationActions({
  registration,
  canWrite,
  actionLoading,
  onView,
  onReview,
  showReceipt = false,
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      <button
        type="button"
        onClick={() => onView(registration)}
        className="inline-flex items-center gap-1 rounded-ga-sm border border-ga-border-subtle px-2 py-1 text-[11px] font-bold text-ga-primary hover:bg-ga-bg-surface-subtle"
      >
        <Eye size={14} /> View
      </button>
      {showReceipt && registration.receipt_path && (
        <a
          href={resolveUploadUrl(registration.receipt_path)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-ga-sm border border-ga-border-subtle px-2 py-1 text-[11px] font-bold text-ga-primary hover:bg-ga-bg-surface-subtle"
        >
          <ExternalLink size={14} /> Receipt
        </a>
      )}
      {canWrite && registration.status === 'pending' && (
        <>
          <button
            type="button"
            disabled={actionLoading === registration.id}
            onClick={() => onReview(registration, 'approve')}
            className="inline-flex items-center gap-1 rounded-ga-sm bg-ga-success px-2 py-1 text-[11px] font-bold text-ga-text-on-primary disabled:opacity-50"
          >
            <CheckCircle size={14} /> Approve
          </button>
          <button
            type="button"
            disabled={actionLoading === registration.id}
            onClick={() => onReview(registration, 'reject')}
            className="inline-flex items-center gap-1 rounded-ga-sm border border-ga-danger/40 px-2 py-1 text-[11px] font-bold text-ga-danger disabled:opacity-50"
          >
            <XCircle size={14} /> Deny
          </button>
        </>
      )}
    </div>
  );
}

export default function TournamentRegistrationsPanel({ tournamentId, onChanged }) {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission(MODULES.TOURNAMENTS, 'write');
  const [filter, setFilter] = useState('pending');
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const requestIdRef = useRef(0);
  const tournamentIdRef = useRef(tournamentId);
  const filterRef = useRef(filter);
  tournamentIdRef.current = tournamentId;
  filterRef.current = filter;

  const load = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    const requestedTournamentId = tournamentIdRef.current;
    const requestedFilter = filterRef.current;
    setLoading(true);
    setError('');
    try {
      const payload = await getTournamentRegistrations(requestedTournamentId, requestedFilter);
      if (requestId === requestIdRef.current) setRegistrations(payload.data || []);
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setRegistrations([]);
        setError(err.message || 'Failed to load registrations');
      }
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => {
      requestIdRef.current += 1;
    };
  }, [filter, load, tournamentId]);

  const review = async (registration, action) => {
    const mutationTournamentId = tournamentIdRef.current;
    const reason = action === 'reject'
      ? window.prompt('Reason for rejection (optional):')
      : undefined;
    setActionLoading(registration.id);
    setError('');
    try {
      const payload = await reviewTournamentRegistration(
        registration.id,
        action,
        action === 'reject' ? { reason } : {}
      );
      if (mutationTournamentId !== tournamentIdRef.current) return;
      await load();
      onChanged?.();
      if (action === 'approve' && payload.data?.whatsappLink) {
        window.open(payload.data.whatsappLink, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setError(err.message || `Failed to ${action} registration`);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div>
      {error && <InfoBanner variant="danger" className="mb-4">{error}</InfoBanner>}

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((item) => (
          <button
            key={item.value || 'all'}
            type="button"
            onClick={() => setFilter(item.value)}
            className={`rounded-ga-full border px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              filter === item.value
                ? 'border-ga-primary bg-ga-primary text-ga-text-on-primary'
                : 'border-ga-border-subtle bg-ga-bg-surface text-ga-text-primary hover:bg-ga-bg-surface-subtle'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-10 text-center text-ga-text-muted">
          <Loader2 className="mx-auto h-7 w-7 animate-spin" />
        </div>
      ) : registrations.length === 0 ? (
        <EmptyState
          card
          icon={Users}
          title="No registrations here yet"
          description="Registrations matching this status will appear here."
        />
      ) : (
        <>
          <div className="space-y-3 md:hidden" data-testid="mobile-registration-list">
            {registrations.map((registration) => (
              <article
                key={registration.id}
                className="rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface p-4 shadow-ga-soft"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold text-ga-text-primary">
                      {registration.team_name}
                    </h3>
                    <p className="mt-1 text-sm text-ga-text-muted">
                      Captain: {registration.captain_name}
                    </p>
                  </div>
                  <StatusPill status={registration.status} size="sm" />
                </div>

                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-ga-text-muted">
                      Contact
                    </dt>
                    <dd className="mt-1 inline-flex items-center gap-1 text-ga-text-primary">
                      <Phone size={13} /> {registration.phone}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-ga-text-muted">
                      Amount paid
                    </dt>
                    <dd className="mt-1 font-semibold text-ga-text-primary">
                      {registration.amount_paid != null
                        ? `₹${Number(registration.amount_paid || 0).toLocaleString()}`
                        : '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase tracking-wide text-ga-text-muted">
                      Submitted
                    </dt>
                    <dd className="mt-1 text-ga-text-primary">
                      {formatDate(registration.submitted_at)}
                    </dd>
                  </div>
                  {(registration.alternate_contact_name || registration.alternate_contact_number) && (
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-ga-text-muted">
                        Alternate contact
                      </dt>
                      <dd className="mt-1 text-ga-text-primary">
                        {[registration.alternate_contact_name, registration.alternate_contact_number]
                          .filter(Boolean)
                          .join(' · ')}
                      </dd>
                    </div>
                  )}
                </dl>

                <div className="mt-4 border-t border-ga-border-subtle pt-3">
                  <RegistrationActions
                    registration={registration}
                    canWrite={canWrite}
                    actionLoading={actionLoading}
                    onView={setSelectedRegistration}
                    onReview={review}
                    showReceipt
                  />
                </div>
              </article>
            ))}
          </div>

          <div
            className="hidden overflow-x-auto rounded-ga-md border border-ga-border-subtle bg-ga-bg-surface shadow-ga-soft md:block"
            data-testid="desktop-registration-table"
          >
          <table className="ga-table w-full text-sm">
            <thead>
              <tr>
                <th className="px-3 py-2.5 text-left">Team</th>
                <th className="px-3 py-2.5 text-left">Captain</th>
                <th className="px-3 py-2.5 text-left">Contact</th>
                <th className="px-3 py-2.5 text-left">Alt. contact</th>
                <th className="px-3 py-2.5 text-left">Amount paid</th>
                <th className="px-3 py-2.5 text-left">Submitted</th>
                <th className="px-3 py-2.5 text-left">Receipt</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((registration) => (
                <tr key={registration.id}>
                  <td className="px-3 py-3 font-semibold text-ga-text-primary">{registration.team_name}</td>
                  <td className="px-3 py-3 text-ga-text-primary">{registration.captain_name}</td>
                  <td className="px-3 py-3 text-ga-text-muted">
                    <span className="inline-flex items-center gap-1">
                      <Phone size={12} /> {registration.phone}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-ga-text-muted">
                    {registration.alternate_contact_name && (
                      <div>
                        <div>{registration.alternate_contact_name}</div>
                        <div className="text-xs">{registration.alternate_contact_number}</div>
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-ga-text-primary">
                    {registration.amount_paid != null
                      ? `₹${Number(registration.amount_paid || 0).toLocaleString()}`
                      : '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-ga-text-muted">
                    {formatDate(registration.submitted_at)}
                  </td>
                  <td className="px-3 py-3">
                    {registration.receipt_path && (
                      <a
                        href={resolveUploadUrl(registration.receipt_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-ga-primary hover:underline"
                      >
                        <ExternalLink size={14} /> View
                      </a>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <StatusPill status={registration.status} size="sm" />
                  </td>
                  <td className="px-3 py-3">
                    <RegistrationActions
                      registration={registration}
                      canWrite={canWrite}
                      actionLoading={actionLoading}
                      onView={setSelectedRegistration}
                      onReview={review}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      <Modal
        open={Boolean(selectedRegistration)}
        onClose={() => setSelectedRegistration(null)}
        title={selectedRegistration?.team_name || 'Registration details'}
        description="Full team registration submission"
        size="lg"
        footer={(
          <div className="flex justify-end">
            <Button variant="secondary" onClick={() => setSelectedRegistration(null)}>
              Close
            </Button>
          </div>
        )}
      >
        {selectedRegistration && (() => {
          const dates = availabilityDates(selectedRegistration.availability_dates);
          const profileUrl = safeExternalUrl(selectedRegistration.team_profile_link);
          return (
            <div className="space-y-6">
              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-ga-text-muted">
                  Team and contacts
                </h3>
                <dl className="mt-3 grid gap-4 sm:grid-cols-2">
                  <DetailItem label="Team name">{selectedRegistration.team_name}</DetailItem>
                  <DetailItem label="Captain">{selectedRegistration.captain_name}</DetailItem>
                  <DetailItem label="Phone">{selectedRegistration.phone}</DetailItem>
                  <DetailItem label="Email">{selectedRegistration.email}</DetailItem>
                  <DetailItem label="Alternate contact">
                    {selectedRegistration.alternate_contact_name || selectedRegistration.alternate_contact_number
                      ? [
                        selectedRegistration.alternate_contact_name,
                        selectedRegistration.alternate_contact_number,
                      ].filter(Boolean).join(' · ')
                      : null}
                  </DetailItem>
                  <DetailItem label="Player count">{selectedRegistration.player_count}</DetailItem>
                  <DetailItem label="Team profile" className="sm:col-span-2">
                    {profileUrl ? (
                      <a
                        href={profileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 break-all font-semibold text-ga-primary hover:underline"
                      >
                        {selectedRegistration.team_profile_link} <ExternalLink size={14} />
                      </a>
                    ) : selectedRegistration.team_profile_link}
                  </DetailItem>
                </dl>
              </section>

              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-ga-text-muted">
                  Availability
                </h3>
                {dates.length > 0 ? (
                  <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                    {dates.map((date) => (
                      <li
                        key={date}
                        className="rounded-ga-sm border border-ga-border-subtle bg-ga-bg-surface-subtle px-3 py-2 text-sm text-ga-text-primary"
                      >
                        {formatAvailabilityDate(date)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-ga-text-muted">No availability dates submitted.</p>
                )}
              </section>

              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-ga-text-muted">
                  Payment and review
                </h3>
                <dl className="mt-3 grid gap-4 sm:grid-cols-2">
                  <DetailItem label="Amount paid">
                    {selectedRegistration.amount_paid != null
                      ? `₹${Number(selectedRegistration.amount_paid || 0).toLocaleString()}`
                      : '—'}
                  </DetailItem>
                  <DetailItem label="Submitted">{formatDate(selectedRegistration.submitted_at)}</DetailItem>
                  <DetailItem label="Status">
                    <StatusPill status={selectedRegistration.status} size="sm" />
                  </DetailItem>
                  <DetailItem label="Payment receipt">
                    {selectedRegistration.receipt_path ? (
                      <a
                        href={resolveUploadUrl(selectedRegistration.receipt_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 font-semibold text-ga-primary hover:underline"
                      >
                        View receipt <ExternalLink size={14} />
                      </a>
                    ) : '—'}
                  </DetailItem>
                  <DetailItem label="Rejection reason" className="sm:col-span-2">
                    {selectedRegistration.rejection_reason}
                  </DetailItem>
                  <DetailItem label="Notes" className="sm:col-span-2">
                    {selectedRegistration.notes}
                  </DetailItem>
                </dl>
              </section>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
